import { Component } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer, toast } from 'react-toastify';
import classnames from 'classnames/bind';
import 'react-toastify/dist/ReactToastify.css';
import styles from './Notification.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
const cln = classnames.bind(styles);

/**
 * This is a wrapper component for [react-toastif](https://github.com/fkhadra/react-toastify) v4.4.3. Read react-tostify readme in order
 * to check correct usage of other properties. Most of the time you will probably use Notification.notify helper
 * to trigger notifications imperatively but you can still use Notification component in a declarative way.
 * You need to remember to handle notification message state in declarative approach by clearing it on alert dismiss (onClose event handler).
 */
class Notification extends Component {
  static propTypes = {
    /** Message to display in the body of the toast. */
    message: PropTypes.string.isRequired,
    // eslint-disable-line
    /** Milliseconds to wait before automatically dismissing notification. */
    timeout: PropTypes.number,
    // eslint-disable-line
    /** (didTimeoutExpire) => {} - callback invoked when the notification is dismissed, either by the user or by the timeout. */
    onClose: PropTypes.func,
    // eslint-disable-line
    /** Visual intent color to apply to element. */
    intent: PropTypes.oneOf(['default', 'success', 'warning', 'info', 'error']),
    // eslint-disable-line
    /** Custom id to set for this notification */
    customId: PropTypes.string,
    /** If true, custom id is created automatically from notification message and intent type. This will prevent
     *  rerendering of the same notifications
     */
    autoUniqueId: PropTypes.bool
  };
  static defaultProps = {
    timeout: 5000,
    onClose: () => {},
    intent: 'success',
    customId: undefined,
    autoUniqueId: false
  };
  componentDidMount() {
    this.renderToaster();
  }
  componentDidUpdate() {
    this.renderToaster();
  }
  renderToaster = () => {
    const {
      intent,
      message,
      customId,
      autoUniqueId,
      timeout,
      ...rest
    } = this.props;
    let id = customId;
    if (!customId && autoUniqueId) {
      id = `${message} ${intent}`;
    }

    // eslint-disable-next-line react/no-unused-class-component-methods
    this.id = toast(message, {
      type: intent,
      toastId: id,
      autoClose: timeout,
      ...rest
    });
  };
  render() {
    return null;
  }
}

/**
 * This container should be used only once in your project
 */
function NotificationContainer(props) {
  return /*#__PURE__*/_jsx(ToastContainer, {
    toastClassName: cln('Notification'),
    bodyClassName: cln('body'),
    progressClassName: cln('progress'),
    position: "top-center",
    className: cln('container'),
    autoClose: 5000,
    icon: false,
    ...props
  });
}
Notification.notify = toast;
Notification.POSITION = toast.POSITION;
Notification.TYPE = toast.TYPE;
Notification.Container = NotificationContainer;
export default Notification;